import { createAsyncThunk } from '@reduxjs/toolkit';
import store from 'app/store';
import { setActiveConfig } from 'features/configurations/configurations-slice';
import axios from 'utils/api';
import { hasUserConfigurationWork } from 'utils/configuration-generation';
import {
  refetchInstance,
  refetchInstanceConfigurations,
  refetchInstanceSettings,
  refetchInstanceSystems,
  refetchProject,
  refetchStructureLevels
} from 'utils/data';
import { notification } from 'utils/ui';
import {
  addConversation,
  addMessage,
  ChatbotConversationCategoryType,
  ChatbotConversationType,
  ChatbotMessageType,
  setActiveConversationUniqueId,
  setCategories,
  setConversations,
  setLoadingCategories,
  setLoadingConversations,
  setLoadingMessages,
  setMessages,
  setProcessingStatus,
  setSendingMessage,
  updateConversationTitle
} from './chatbot-slice';

const POLLING_INTERVAL_MS = 2000;
const POLLING_TIMEOUT_MS = 5 * 60 * 1000;

export const fetchChatbotCategories = createAsyncThunk(
  'chatbot/fetchCategories',
  async () => {
    try {
      store.dispatch(setLoadingCategories(true));
      const response = await axios.get<{
        categories: ChatbotConversationCategoryType[];
      }>('ai/chatbot/categories');
      store.dispatch(setCategories(response.data.categories));
      return response.data.categories;
    } catch (error) {
      notification('Failed to fetch chatbot categories', 'error');
      throw error;
    } finally {
      store.dispatch(setLoadingCategories(false));
    }
  }
);

export const fetchChatbotConversations = createAsyncThunk(
  'chatbot/fetchConversations',
  async (params: { structureUniqueId: string; categoryId?: number }) => {
    try {
      store.dispatch(setLoadingConversations(true));
      const query = new URLSearchParams({
        structureUniqueId: params.structureUniqueId
      });
      if (params.categoryId) {
        query.set('categoryId', String(params.categoryId));
      }
      const response = await axios.get<{
        conversations: ChatbotConversationType[];
      }>(`ai/chatbot/conversations?${query.toString()}`);
      store.dispatch(setConversations(response.data.conversations));
      return response.data.conversations;
    } catch (error) {
      notification('Failed to fetch conversations', 'error');
      throw error;
    } finally {
      store.dispatch(setLoadingConversations(false));
    }
  }
);

export const createChatbotConversation = createAsyncThunk(
  'chatbot/createConversation',
  async (params: {
    structureUniqueId: string;
    categoryId: number;
    title?: string;
  }) => {
    try {
      const response = await axios.post<{
        conversation: ChatbotConversationType;
      }>('ai/chatbot/conversations', {
        structureUniqueId: params.structureUniqueId,
        categoryId: params.categoryId,
        title: params.title ?? null
      });
      const { conversation } = response.data;
      store.dispatch(addConversation(conversation));
      store.dispatch(setActiveConversationUniqueId(conversation.uniqueId));
      store.dispatch(setMessages([]));
      return conversation;
    } catch (error) {
      notification('Failed to create conversation', 'error');
      throw error;
    }
  }
);

export const fetchChatbotMessages = createAsyncThunk(
  'chatbot/fetchMessages',
  async (conversationUniqueId: string) => {
    try {
      store.dispatch(setLoadingMessages(true));
      const response = await axios.get<{
        messages: ChatbotMessageType[];
        status: string;
      }>(`ai/chatbot/conversations/${conversationUniqueId}/messages`);
      store.dispatch(setMessages(response.data.messages));
      return response.data.messages;
    } catch (error) {
      notification('Failed to fetch messages', 'error');
      throw error;
    } finally {
      store.dispatch(setLoadingMessages(false));
    }
  }
);

async function handleChatbotRefetch(
  target: string,
  structureUniqueId: string,
  projectId: number
) {
  switch (target) {
    case 'structure':
    case 'structure:space':
    case 'structure:section':
    case 'structure:level':
      await refetchStructureLevels(structureUniqueId);
      break;
    case 'instance':
      await refetchInstance();
      break;
    case 'instance:configurations':
    case 'configuration':
      await refetchInstanceConfigurations();
      break;
    case 'instance:systems':
      await refetchInstanceSystems();
      break;
    case 'instance:settings':
      await refetchInstanceSettings();
      break;
    case 'project':
    case 'project:details':
      await refetchProject(projectId);
      break;
    default:
      break;
  }
}

export async function pollChatbotMessages(
  conversationUniqueId: string,
  structureUniqueId: string,
  projectId: number,
  initialLastMessageId: number,
  signal?: AbortSignal,
  timeoutMs: number = POLLING_TIMEOUT_MS
): Promise<'completed' | 'failed'> {
  const startTime = Date.now();
  let lastMessageId = initialLastMessageId;
  let isFirstPoll = true;

  return new Promise((resolve, reject) => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const handleAbort = () => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
        timeoutId = undefined;
      }
      reject(new Error('Polling aborted'));
    };

    const cleanup = () => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
        timeoutId = undefined;
      }
      signal?.removeEventListener('abort', handleAbort);
    };

    if (signal?.aborted) {
      reject(new Error('Polling aborted'));
      return;
    }

    signal?.addEventListener('abort', handleAbort, { once: true });

    const poll = async () => {
      if (signal?.aborted) return;
      try {
        if (Date.now() - startTime > timeoutMs) {
          store.dispatch(setProcessingStatus('failed'));
          store.dispatch(setSendingMessage(false));
          notification('Chatbot processing timed out', 'error');
          cleanup();
          reject(new Error('Polling timeout'));
          return;
        }

        const response = await axios.get<{
          messages: ChatbotMessageType[];
          status: string;
        }>(
          `ai/chatbot/conversations/${conversationUniqueId}/messages?afterId=${lastMessageId}`,
          { signal }
        );

        const { messages: newMessages, status } = response.data;

        if (newMessages.length > 0) {
          if (isFirstPoll) {
            const existing = store
              .getState()
              .chatbot.messages.filter((m) => m.id <= initialLastMessageId);
            store.dispatch(setMessages([...existing, ...newMessages]));
            isFirstPoll = false;
          } else {
            for (const msg of newMessages) {
              store.dispatch(addMessage(msg));
            }
          }

          const allTargets: string[] = [];
          for (const msg of newMessages) {
            if (msg.refetchTargets) {
              try {
                const targets: string[] = JSON.parse(msg.refetchTargets);
                allTargets.push(...targets);
              } catch {
                // refetchTargets parse failure is non-fatal
              }
            }
          }

          lastMessageId = newMessages[newMessages.length - 1].id;

          const uniqueTargets = [...new Set(allTargets)];
          if (uniqueTargets.length > 0) {
            await Promise.all(
              uniqueTargets.map((target) =>
                handleChatbotRefetch(target, structureUniqueId, projectId)
              )
            );
          }

          for (const msg of newMessages) {
            if (msg.metadata) {
              try {
                const meta = JSON.parse(msg.metadata);
                if (meta.selectConfigurationUniqueId) {
                  const { configurations: configs, activeConfig } =
                    store.getState().configurations;
                  const activeConfiguration = configs[activeConfig];
                  if (!hasUserConfigurationWork(activeConfiguration)) {
                    const idx = configs.findIndex(
                      (c) => c.uniqueId === meta.selectConfigurationUniqueId
                    );
                    if (idx >= 0) {
                      store.dispatch(setActiveConfig(idx));
                    }
                  }
                }
              } catch {
                // metadata parse failure is non-fatal
              }
            }
          }
        }

        if (status === 'completed' || status === 'failed') {
          store.dispatch(setProcessingStatus(status as 'completed' | 'failed'));
          store.dispatch(setSendingMessage(false));

          if (status === 'completed') {
            const state = store.getState().chatbot;
            const conversation = state.conversations.find(
              (c) => c.uniqueId === conversationUniqueId
            );
            if (conversation && !conversation.title) {
              const userMessages = state.messages.filter(
                (m) => m.role === 'user'
              );
              const firstUserMsg = userMessages[0]?.content;
              if (firstUserMsg) {
                const title =
                  firstUserMsg.length > 50
                    ? `${firstUserMsg.substring(0, 47)}...`
                    : firstUserMsg;
                store.dispatch(
                  updateConversationTitle({
                    uniqueId: conversationUniqueId,
                    title
                  })
                );
              }
            }
          }

          cleanup();
          resolve(status as 'completed' | 'failed');
          return;
        }

        timeoutId = setTimeout(poll, POLLING_INTERVAL_MS);
      } catch (error) {
        if (signal?.aborted) return;
        // eslint-disable-next-line no-console
        console.error('Error polling chatbot messages:', error);
        timeoutId = setTimeout(poll, POLLING_INTERVAL_MS);
      }
    };

    timeoutId = setTimeout(poll, POLLING_INTERVAL_MS);
  });
}

export const sendChatbotMessage = createAsyncThunk(
  'chatbot/sendMessage',
  async (params: {
    conversationUniqueId: string;
    message: string;
    displayMessage?: string;
    structureUniqueId: string;
    projectId: number;
    instanceUniqueId?: string;
    selectedConfigurationUniqueId?: string;
  }) => {
    try {
      store.dispatch(setSendingMessage(true));
      store.dispatch(setProcessingStatus('processing'));

      const existingMessages = store.getState().chatbot.messages;
      const lastRealId =
        existingMessages.length > 0
          ? Math.max(...existingMessages.map((m) => m.id))
          : 0;

      const userMessage: ChatbotMessageType = {
        id: Date.now(),
        conversationId: 0,
        role: 'user',
        content: params.message,
        orchestrateRequestId: null,
        refetchTargets: null,
        metadata: params.displayMessage
          ? JSON.stringify({ displayMessage: params.displayMessage })
          : null,
        createdAt: new Date().toISOString()
      };
      store.dispatch(addMessage(userMessage));

      await axios.post<{ status: string }>('ai/chatbot/message', {
        conversationUniqueId: params.conversationUniqueId,
        message: params.message,
        displayMessage: params.displayMessage,
        structureUniqueId: params.structureUniqueId,
        projectId: params.projectId,
        instanceUniqueId: params.instanceUniqueId,
        selectedConfigurationUniqueId: params.selectedConfigurationUniqueId
      });

      await pollChatbotMessages(
        params.conversationUniqueId,
        params.structureUniqueId,
        params.projectId,
        lastRealId
      );

      return { status: 'completed' };
    } catch (error) {
      store.dispatch(setProcessingStatus('failed'));
      notification('Failed to send message', 'error');
      throw error;
    } finally {
      store.dispatch(setSendingMessage(false));
    }
  }
);
